import Close from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { OrderState } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import Checkbox from '~components/Checkbox/Checkbox';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { Drawer } from '~components/Drawer/src/ui';
import { Body } from '~components/Drawer/src/ui/Body';
import { Footer } from '~components/Drawer/src/ui/Footer';
import { Header } from '~components/Drawer/src/ui/Header';
import DateInput from '~components/FormFields/v2/DateInput';
import {
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { OrderStateChip } from '~components/Order/OrderStatusChip';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { GeofenceStartAdornment } from '~pages/Settings/Administration/Sites/components/GeofenceStartAdornment';
import { dateFormat } from '~utils/dateTime';

// One impacted order in the selective-rate-apply drawer. `name`/`pickupSiteName`/
// `dropoffSiteName` are richer-row fields sourced from the preview endpoint;
// `dispatchNumberName` joins the title the way the order summary cards do
// (absent until the preview serializer sends it — the title degrades to name-only).
export interface ImpactedOrderRow {
  id: string;
  orderNumber: string;
  state?: string;
  loadAt?: string | null;
  name?: string | null;
  dispatchNumberName?: string | null;
  materialName?: string | null;
  pickupSiteName?: string | null;
  dropoffSiteName?: string | null;
  // TRE-16125: server-reported durable-dereference state (BE `dereferenced` on the rate-impact
  // order read). True when the order already owns its rates (decoupled from the project cards until
  // re-referenced). Such rows render UNCHECKED by default; re-checking one re-references it. Absent
  // (undefined) on endpoints that don't carry the flag (e.g. the first-card prospective preview) —
  // treated as not-dereferenced, which is the correct default (a project with no persisted card has
  // no dereferenced orders).
  dereferenced?: boolean;
}

// TRE-16125: the impact drawer's confirm payload.
//   - `skippedOrderIds` — the transient within-run exclusion audience (unchanged; the delete-card
//     flow uses ONLY this).
//   - `referencedOrderIds` — the COMPLETE in-range referenced set: every displayed (in-range) row
//     that is CHECKED. Populated only in the selective-apply (referenced-model) flow; the BE
//     dereferences everything reachable NOT in this list (in-range unchecked AND out-of-range/past),
//     and re-references any listed order that was previously dereferenced. The single-list set-math
//     that supersedes the old dereference/re-reference deltas. Empty (unused) in the
//     delete-card flow, which passes no referenced model.
//   - `confirmFullDereference` — true ONLY when `referencedOrderIds` is empty AND the user confirmed
//     decoupling every order. The BE requires it to accept a zero-referenced request.
//   - `from`/`to` — the selected date range (`YYYY-MM-DD`, TRE-15583), still forwarded on the SRS
//     write so the BE scopes the in-run cascade.
export interface RateChangeImpactConfirmResult {
  skippedOrderIds: string[];
  referencedOrderIds: string[];
  confirmFullDereference?: boolean;
  from?: string;
  to?: string;
}

// The date-range narrowing form (From/To pickers). Lives in its own useForm —
// it drives the impacted-orders refetch, not a submit payload.
interface DateRangeFormValues {
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

interface RateChangeImpactDrawerProps {
  isOpen: boolean;
  orders: ImpactedOrderRow[];
  title?: string;
  // Intro copy shown above the selection tree. Defaults to the value-edit
  // guidance; the delete-card flow overrides it with delete-specific copy.
  intro?: string;
  // Optional caveat rendered below the selection tree (e.g. the delete-card
  // rematch warning). Omitted for the value-edit path.
  warning?: string;
  confirmLabel?: string;
  confirmColor?: 'brandV2Yellow' | 'brandV2Red';
  isBusy?: boolean;
  // TRE-15583: opt in to the date-range filter + richer rows. Off by default so the
  // shared delete-card warning flow (FreightRatesManager) is unaffected — it renders
  // this same drawer and must not gain a date filter (its legacy audience is
  // state-agnostic and largely undated).
  showDateRange?: boolean;
  // TRE-16125: opt in to the single referenced-list model (the selective-apply flow). When true,
  // confirm reports `referencedOrderIds` (the complete in-range CHECKED set) and, if that set would
  // be empty, shows the full-dereference confirmation before reporting `confirmFullDereference`.
  // OFF (default) for the shared delete-card warning flow, which reports only `skippedOrderIds` and
  // must NEVER durably dereference or pop the full-dereference confirm.
  isReferencedModel?: boolean;
  // TRE-16125: true when the caller forces an additional order into the referenced set out-of-band
  // (the order-origin edit sends `origin_order_id`, which the BE keeps referenced). With one forced
  // in, an empty in-range CHECKED set is NOT a full dereference — so the full-dereference confirm is
  // suppressed (popping it would tell the user "decouple everything" when the origin survives).
  hasOriginReference?: boolean;
  // Which form family opened the drawer. Picks the where-will-this-apply banner
  // copy in the selective-apply flow and rides as the `scope` property on the
  // selective-apply analytics events. Required so every host names its scope
  // (the delete-card flow's events and banner are gated off `isReferencedModel`,
  // but it still passes its widget scope).
  scope: 'order' | 'project';
  // Re-fetch the (server-filtered) impacted orders for a new date range. Date scoping
  // lives on the BE — a picker change asks the endpoint for the in-range orders rather
  // than filtering the seed list client-side. The delete-card flow passes a no-op (its
  // picker never renders).
  fetchOrders: (
    from?: string,
    to?: string,
    signal?: AbortSignal,
  ) => Promise<ImpactedOrderRow[]>;
  // Reports the confirm payload: the transient within-run skip set (`skippedOrderIds` — the BE
  // folds in out-of-range orders itself), the COMPLETE in-range referenced list
  // (`referencedOrderIds`) plus `confirmFullDereference` when that list is empty, and the selected
  // date range (`YYYY-MM-DD`). The Project always receives the change and is never listed.
  onConfirm: (result: RateChangeImpactConfirmResult) => void;
  onClose: () => void;
}

// The selective-rate-apply impact side sheet. Shows each cascade-reachable
// order as a deselectable row; the project itself always receives the change (rate
// cards live on the project, so it is never listed as a choice).
// An optional date range narrows the list: on a picker change the sheet re-fetches
// the server-filtered orders (`fetchOrders`), and on confirm it hands the range to the
// caller so the BE can pin out-of-range orders. Tri-state parent mirrors the
// DriverPayV2 selection model.
export default function RateChangeImpactDrawer({
  isOpen,
  orders,
  title,
  intro,
  warning,
  confirmLabel,
  confirmColor = 'brandV2Yellow',
  isBusy = false,
  showDateRange = false,
  isReferencedModel = false,
  hasOriginReference = false,
  scope,
  fetchOrders,
  onConfirm,
  onClose,
}: RateChangeImpactDrawerProps) {
  // Date-range narrowing. Default From = today, To = open — "apply from today forward":
  // the caller does the initial fetch with the same default, so the seed `orders` are
  // already today-forward. Clearing From widens back to all orders (BE-side).
  // The range lives in its own small react-hook-form so the pickers can be the shared
  // v2 DateInput (which binds via useController) — it is not part of any submit payload.
  const { control: dateRangeControl, reset: resetDateRange } =
    useForm<DateRangeFormValues>({
      defaultValues: { dateFrom: dayjs.tz(), dateTo: null },
    });
  const dateFrom = useWatch({ control: dateRangeControl, name: 'dateFrom' });
  const dateTo = useWatch({ control: dateRangeControl, name: 'dateTo' });
  // The rows currently displayed. Seeded from the `orders` prop and replaced wholesale
  // by `fetchOrders` results on a range change (the list is now server-filtered).
  const [rows, setRows] = useState<ImpactedOrderRow[]>(orders);
  // The user's EXPLICIT unchecks. Skip set = these only; the BE folds in out-of-range
  // orders itself, so the FE no longer unions a client-computed out-of-range set.
  // TRE-16125: also seeded with any already-`dereferenced` orders so they render UNCHECKED by
  // default (they already own their rates) — see `seedDereferencedDefaults`.
  const [explicitlyUnchecked, setExplicitlyUnchecked] = useState<Set<string>>(new Set());
  // TRE-16125: ids whose `dereferenced` default has already been applied to `explicitlyUnchecked`.
  // Applying it exactly once per order preserves a user's re-check across a date-range refetch that
  // re-returns the same order (the default must not silently re-uncheck it). A row that arrives
  // server-`dereferenced` starts UNCHECKED (it already keeps its own rates); leaving it unchecked at
  // save omits it from `referencedOrderIds` → the BE keeps it dereferenced (no-op). Re-checking it
  // puts it back into `referencedOrderIds` → the BE re-references it.
  const seededDereferencedRef = useRef<Set<string>>(new Set());
  // Seed the unchecked default for any newly-seen dereferenced order. Idempotent per order.
  const seedDereferencedDefaults = useCallback((list: ImpactedOrderRow[]) => {
    const newlySeen = list.filter(
      (o) => o.dereferenced && !seededDereferencedRef.current.has(o.id),
    );
    if (newlySeen.length === 0) return;
    setExplicitlyUnchecked((prev) => {
      const next = new Set(prev);
      newlySeen.forEach((o) => {
        next.add(o.id);
        seededDereferencedRef.current.add(o.id);
      });
      return next;
    });
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  // Staged confirm result awaiting the user's full-dereference confirmation. Set when a
  // selective-apply confirm would reference ZERO orders; while non-null the confirmation
  // dialog is shown and the save is paused. Cleared on cancel (back to the drawer) or on
  // confirm (re-emitted with `confirmFullDereference: true`).
  const [pendingFullDereference, setPendingFullDereference] =
    useState<RateChangeImpactConfirmResult | null>(null);
  // Fail closed: a failed range refetch sets this, which clears the list, blocks confirm,
  // and shows an error — so the user can never apply against a stale/undisclosed order set.
  const [fetchError, setFetchError] = useState(false);
  // Rapid picker changes fire overlapping fetches; each one aborts the one before it, so a slow
  // older response can't show orders (or an error) for a range the pickers no longer reflect — a
  // fail-closed hole (a stale success could re-enable Apply).
  const issueRefetchSignal = useLatestRequestSignal();

  // Selective-apply analytics (gated on `isReferencedModel` so the delete-card
  // warning flow, which reuses this drawer, stays silent). The two refs record
  // whether the user narrowed the date range / touched the selection during
  // this open, and ride out on the `applied` event.
  const dateRangeChangedRef = useRef(false);
  const selectionChangedRef = useRef(false);
  useEffect(() => {
    if (isOpen && isReferencedModel) {
      AnalyticsController.track('rate_change_impact_opened', { scope });
    }
  }, [isOpen, isReferencedModel, scope]);

  // Keep `rows` in sync with the seed `orders`. Load-bearing for the delete-card flow,
  // where this drawer is always mounted and its `orders` arrive after mount (via a ref +
  // re-render). A refetch's `setRows` isn't clobbered: it doesn't change the `orders`
  // prop, so this effect doesn't re-run (showDateRange=false there → no refetch anyway).
  useEffect(() => {
    setRows(orders);
    seedDereferencedDefaults(orders);
  }, [orders, seedDereferencedDefaults]);

  // Select-all / deselect-all is scoped to the current (server-filtered) rows only.
  const uncheckedCount = rows.filter((o) => explicitlyUnchecked.has(o.id)).length;
  const allChecked = uncheckedCount === 0;

  const toggleOrder = (id: string) => {
    selectionChangedRef.current = true;
    setExplicitlyUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    selectionChangedRef.current = true;
    setExplicitlyUnchecked((prev) => {
      const next = new Set(prev);
      if (uncheckedCount === 0) {
        // all checked → uncheck them all
        rows.forEach((o) => next.add(o.id));
      } else {
        // some/all unchecked → check them all
        rows.forEach((o) => next.delete(o.id));
      }
      return next;
    });
  };

  // Re-fetch the server-filtered orders for a new range (bounds already formatted
  // to `YYYY-MM-DD`, or undefined when cleared) and swap in the result.
  const refetch = useCallback(
    async (from?: string, to?: string) => {
      const signal = issueRefetchSignal();
      setIsLoading(true);
      try {
        const result = await fetchOrders(from, to, signal);
        if (signal.aborted) return;
        setRows(result);
        seedDereferencedDefaults(result);
        setFetchError(false);
      } catch {
        // Fail closed: if the range refetch fails we can't disclose the in-range orders, so
        // clear the list and flag the error — confirm is blocked below so the user can't apply
        // against a stale set (least astonishment, matching the submit-gate fail-closed).
        if (signal.aborted) return;
        setRows([]);
        setFetchError(true);
      } finally {
        // Only the latest request owns the loading state — a stale resolve mustn't hide the
        // spinner while the newer fetch is still running.
        if (!signal.aborted) setIsLoading(false);
      }
    },
    [fetchOrders, issueRefetchSignal, seedDereferencedDefaults],
  );

  // Refetch when the pickers change. The refs hold the previously-fetched bounds —
  // initialized to the defaults so the effect no-ops on mount (the caller's initial
  // fetch already used the same default range) and on unrelated re-renders.
  const previousDateFromRef = useRef<string | undefined>(dayjs.tz().format('YYYY-MM-DD'));
  const previousDateToRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const from = dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined;
    const to = dateTo ? dateTo.format('YYYY-MM-DD') : undefined;
    if (from === previousDateFromRef.current && to === previousDateToRef.current) {
      return;
    }
    previousDateFromRef.current = from;
    previousDateToRef.current = to;
    dateRangeChangedRef.current = true;
    refetch(from, to);
  }, [dateFrom, dateTo, refetch]);

  // Build the confirm payload (TRE-16125, single referenced-list model).
  //   - `referencedOrderIds` — the COMPLETE in-range CHECKED set: every displayed row (already the
  //     full, cursor-exhausted in-range set) that is not in `explicitlyUnchecked`. In-range unchecked
  //     is omitted → the BE dereferences it; out-of-range/past orders are never in `rows` → omitted →
  //     dereferenced (the forward-looking default). The date range is thus the final word:
  //     an order the user checked at a wider range but that is out of range at save is omitted.
  //   - `skippedOrderIds` — the transient within-run skip set (the BE folds in out-of-range orders
  //     itself), still forwarded on the SRS write.
  // Full-dereference guard: a selective-apply save whose referenced set would be EMPTY
  // (and with no origin forced in) means "decouple everything" — pause and confirm before emitting
  // `confirmFullDereference: true`. Non-referenced-model callers (delete-card) skip all of this.
  const buildConfirmResult = (): RateChangeImpactConfirmResult => ({
    skippedOrderIds: Array.from(explicitlyUnchecked),
    referencedOrderIds: isReferencedModel
      ? rows.filter((o) => !explicitlyUnchecked.has(o.id)).map((o) => o.id)
      : [],
    from: dateFrom?.format('YYYY-MM-DD'),
    to: dateTo?.format('YYYY-MM-DD'),
  });

  // Every path that actually emits the confirm payload funnels through here,
  // so the `applied` analytics event fires exactly once per apply — whether
  // direct or after the full-dereference confirmation.
  const emitConfirm = (result: RateChangeImpactConfirmResult) => {
    if (isReferencedModel) {
      AnalyticsController.track('rate_change_impact_applied', {
        scope,
        order_count: rows.length,
        deselected_order_count: rows.filter((o) => explicitlyUnchecked.has(o.id)).length,
        selection_changed: selectionChangedRef.current,
        date_range_changed: dateRangeChangedRef.current,
        full_dereference: Boolean(result.confirmFullDereference),
      });
    }
    onConfirm(result);
  };

  const handleConfirm = () => {
    const result = buildConfirmResult();
    // Would this leave zero orders referenced? Only meaningful in the referenced model, and NOT a
    // full dereference when the caller forces an order in out-of-band (order-origin `origin_order_id`).
    const wouldFullyDereference =
      isReferencedModel && result.referencedOrderIds.length === 0 && !hasOriginReference;
    if (wouldFullyDereference) {
      setPendingFullDereference(result);
      return;
    }
    emitConfirm(result);
  };

  // Full-dereference confirmed: re-emit with the BE-required flag set.
  const handleConfirmFullDereference = () => {
    if (!pendingFullDereference) return;
    emitConfirm({ ...pendingFullDereference, confirmFullDereference: true });
    setPendingFullDereference(null);
  };

  const handleClose = () => {
    setExplicitlyUnchecked(new Set());
    setPendingFullDereference(null);
    // Reset the analytics interaction memory so a reopen reports its own
    // range/selection activity, not the abandoned attempt's.
    dateRangeChangedRef.current = false;
    selectionChangedRef.current = false;
    // Clear the dereferenced-seed memo so a reopen re-applies the unchecked default freshly.
    seededDereferencedRef.current = new Set();
    // Reset the range to its defaults AND sync the previous-value refs, so the
    // reset itself doesn't read as a change and fire a stray refetch.
    previousDateFromRef.current = dayjs.tz().format('YYYY-MM-DD');
    previousDateToRef.current = undefined;
    resetDateRange({ dateFrom: dayjs.tz(), dateTo: null });
    onClose();
  };

  // Title composed the way the order summary cards do: dispatch number • name
  // (whichever are present — dispatch first, matching the dispatch card).
  // Falls back to the ORD number when neither exists (e.g. delete-flow rows),
  // so the row always has a primary line.
  const titleLine = (order: ImpactedOrderRow) => {
    const fragments = [order.dispatchNumberName, order.name].filter(Boolean);
    return fragments.length > 0 ? fragments.join(' • ') : order.orderNumber;
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        sx={{
          // MUI stacks temporary Drawers at zIndex.drawer (1200), BELOW Dialogs at
          // zIndex.modal (1300) — but this sheet opens from the project form dialog's
          // submit, so it must paint above that modal.
          zIndex: (theme) => theme.zIndex.modal + 1,
          '& .MuiDrawer-paper': {
            // Side-sheet width: fits the five-column order table (title/ORD number,
            // status chip, date, material, stacked pickup/dropoff) comfortably,
            // while keeping the form visible behind.
            width: '840px',
            maxWidth: '100%',
          },
        }}
      >
        <Header
          title={
            title ? (
              <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
            ) : (
              // 18px mirrors the app's dialog-title scale (e.g. Update Project).
              <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
                {t('project.form.rate_change_impact_title')}
              </Typography>
            )
          }
        >
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Header>

        <Body>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            {/* Where-will-this-apply banner, selective-apply flow only (the delete-card
                flow supplies its own `intro` copy instead). The project always receives
                the change; an order-origin edit also lands on the origin order + jobs. */}
            {isReferencedModel && (
              // mb adds to the Stack's 12px gap — the banner and guidance line
              // breathe at ~20px while the rest of the sheet keeps the tighter rhythm.
              <Alert severity="info" sx={{ mb: 1 }}>
                {scope === 'order'
                  ? t('project.form.rate_change_impact_banner_order')
                  : t('project.form.rate_change_impact_banner_project')}
              </Alert>
            )}

            {intro ? <Typography>{intro}</Typography> : null}

            {isReferencedModel && (
              <Typography sx={{ mb: 1 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('project.form.rate_change_impact_select_heading')}
                </Box>{' '}
                {t('project.form.rate_change_impact_select_detail')}
              </Typography>
            )}

            {/* Optional date-range narrowing (TRE-15583). Gated so the delete-card
                warning flow doesn't get a date filter. A picker change re-fetches the
                server-filtered orders rather than filtering the seed list here. */}
            {showDateRange && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ whiteSpace: 'nowrap' }}>
                  {t('project.form.rate_change_impact_date_range_label')}
                </Typography>
                {/* Fixed picker widths keep the inline label + "to" row from
                    stretching the full-width DateInputs across the 840px sheet. */}
                <Box sx={{ width: '180px' }}>
                  <DateInput
                    control={dateRangeControl}
                    dataUi="rate-change-impact-date-from"
                    isClearable
                    maxDate={dateTo ?? undefined}
                    name="dateFrom"
                    // The calendar popper defaults to zIndex.modal (1300); this drawer
                    // sits at modal + 1 to clear the project form dialog, so the popper
                    // must go one higher or it opens invisibly behind the drawer.
                    popperSx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                  />
                </Box>
                <Typography sx={{ color: 'text.secondary' }}>{t('common.to')}</Typography>
                <Box sx={{ width: '180px' }}>
                  <DateInput
                    control={dateRangeControl}
                    dataUi="rate-change-impact-date-to"
                    isClearable
                    minDate={dateFrom ?? undefined}
                    name="dateTo"
                    placeholder={t('project.form.rate_change_impact_date_any')}
                    // Same zIndex.modal + 2 as the From picker — see comment there.
                    popperSx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
                  />
                </Box>
              </Box>
            )}

            {fetchError ? (
              <Box sx={{ py: 2, pl: 1 }}>
                <Typography variant="body2" color="error">
                  {t('project.form.rate_change_impact_preview_failed')}
                </Typography>
              </Box>
            ) : isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : rows.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                  >
                    {t('project.form.rate_change_impact_orders_header', {
                      count: rows.length,
                    })}
                  </Typography>
                  <SmallButton
                    variant="text"
                    onClick={toggleAll}
                    sx={(theme) => ({ color: theme.brandV2.colors.treadBlue })}
                  >
                    {allChecked ? t('common.deselect_all') : t('common.select_all')}
                  </SmallButton>
                </Box>

                {/* Fixed layout so long names truncate (ellipsis) instead of
                    stretching their column; widths tuned for the 720px sheet. */}
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        [`& .MuiTableCell-head`]: {
                          color: 'text.secondary',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        },
                      }}
                    >
                      <TableCell sx={{ width: '32%' }}>{t('common.order')}</TableCell>
                      <TableCell sx={{ width: '15%' }}>{t('common.status')}</TableCell>
                      <TableCell sx={{ width: '19%' }}>{t('common.date')}</TableCell>
                      <TableCell sx={{ width: '16%' }}>
                        {t('form_fields.material')}
                      </TableCell>
                      <TableCell sx={{ width: '18%' }}>{t('common.sites')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((order) => {
                      const checked = !explicitlyUnchecked.has(order.id);
                      return (
                        <TableRow key={order.id}>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                              }}
                            >
                              <Checkbox
                                isChecked={checked}
                                onChange={() => toggleOrder(order.id)}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <OverflowAwareText sx={{ fontWeight: 600 }}>
                                  {titleLine(order)}
                                </OverflowAwareText>
                                {/* Skip the sub-line when the title already fell back to
                                    the ORD number — it would just repeat it. */}
                                {titleLine(order) !== order.orderNumber ? (
                                  <OverflowAwareText sx={{ color: 'text.secondary' }}>
                                    {order.orderNumber}
                                  </OverflowAwareText>
                                ) : null}
                                {/* TRE-16125: mark an already-dereferenced order so its unchecked row
                                    reads as durably decoupled ("keeps its own rates") rather than as a
                                    stale UI state — re-checking it re-references it. */}
                                {order.dereferenced ? (
                                  <Chip
                                    label={t(
                                      'project.form.rate_change_impact_dereferenced_badge',
                                    )}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{
                                      mt: 0.5,
                                      height: 'auto',
                                      borderRadius: '4px',
                                      '& .MuiChip-label': {
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                      },
                                    }}
                                  />
                                ) : null}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {order.state ? (
                              <OrderStateChip
                                hideDropdownArrow
                                state={order.state as OrderState}
                              />
                            ) : (
                              <OverflowAwareText>—</OverflowAwareText>
                            )}
                          </TableCell>
                          <TableCell>
                            <OverflowAwareText>
                              {order.loadAt
                                ? dateFormat(order.loadAt, 'MMM D · h:mm A')
                                : '—'}
                            </OverflowAwareText>
                          </TableCell>
                          <TableCell>
                            <OverflowAwareText>
                              {order.materialName ?? '—'}
                            </OverflowAwareText>
                          </TableCell>
                          <TableCell>
                            {/* Mirrors the dispatch jobs table's Pickup & Dropoff
                                column: one icon+name line per waypoint. */}
                            {[order.pickupSiteName, order.dropoffSiteName].map(
                              (siteName, index) => (
                                <Box
                                  key={index}
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <GeofenceStartAdornment
                                    geofenceType={null}
                                    isLoading={false}
                                    sx={{ pr: 0.5 }}
                                  />
                                  <OverflowAwareText>{siteName ?? '—'}</OverflowAwareText>
                                </Box>
                              ),
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            ) : showDateRange ? (
              <Box sx={{ py: 1.5, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('project.form.rate_change_impact_no_orders_in_range')}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('project.form.rate_change_impact_no_orders_in_range_hint')}
                </Typography>
              </Box>
            ) : null}

            {warning ? <Typography>{warning}</Typography> : null}
          </Stack>
        </Body>

        <Footer>
          <SmallButton
            variant="outlined"
            color="secondary"
            disabled={isBusy}
            onClick={handleClose}
          >
            {t('actions.cancel')}
          </SmallButton>
          <SmallLoadingButton
            color={confirmColor}
            loading={isBusy}
            onClick={handleConfirm}
            variant="contained"
            // Fail closed: block apply while the impacted-order list couldn't be loaded or is
            // still refetching for a new range — the user must see the disclosed set before applying.
            disabled={fetchError || isLoading}
          >
            {confirmLabel ?? t('project.form.rate_change_impact_confirm')}
          </SmallLoadingButton>
        </Footer>
      </Drawer>

      {/* Full-dereference guard: shown only in the referenced model when a save would leave
          ZERO orders referenced. Confirming decouples every order (they keep their current rates) and
          sends `confirm_full_dereference: true`, which the BE requires to accept a zero-referenced
          request. Sits at modal + 2 to clear the drawer (modal + 1), matching the DateInput poppers. */}
      <ConfirmationDialog
        isOpen={pendingFullDereference !== null}
        isBusy={isBusy}
        onClose={() => setPendingFullDereference(null)}
        onConfirm={handleConfirmFullDereference}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        title={t('project.form.rate_change_full_dereference_title')}
        confirmButtonProps={{
          color: confirmColor,
          children: t('project.form.rate_change_full_dereference_confirm'),
        }}
      >
        <Typography>{t('project.form.rate_change_full_dereference_body')}</Typography>
      </ConfirmationDialog>
    </>
  );
}
